import { useQuery, useMutation, useQueryClient } from 'react-query'
import { RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react'
import { api } from '../services/api'

const Sync = () => {
  const queryClient = useQueryClient()

  const { data: syncStatus, error: statusError } = useQuery('sync-status', () =>
    api.get('/api/sync/status').then(res => res.data),
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const { data: syncHistory, error: historyError } = useQuery('sync-history', () =>
    api.get('/api/sync/history').then(res => res.data),
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const syncMutation = useMutation(
    (platform: string) => api.post(`/api/sync/platform/${platform}`).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sync-status')
        queryClient.invalidateQueries('sync-history')
      },
    }
  )

  const syncAllMutation = useMutation(
    () => api.post('/api/sync/all').then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sync-status')
        queryClient.invalidateQueries('sync-history')
      },
    }
  )

  const platforms = syncStatus?.data || []

  if (statusError || historyError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Sync</h1>
        </div>
        <div className="card">
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <div className="text-sm">Sync data temporarily unavailable</div>
              <div className="text-xs mt-1">Please try again later</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success-600" />
      case 'error':
        return <XCircle className="h-5 w-5 text-danger-600" />
      default:
        return <Clock className="h-5 w-5 text-warning-600" />
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Sync Management</h1>
        <button
          onClick={() => syncAllMutation.mutate()}
          disabled={syncAllMutation.isLoading}
          className="btn btn-primary"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncAllMutation.isLoading ? 'animate-spin' : ''}`} />
          Sync All Platforms
        </button>
      </div>

      {/* Platform Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {platforms.map((platform: any) => (
          <div key={platform.platform} className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {platform.platform}
              </h3>
              {getStatusIcon(platform.status)}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${
                  platform.status === 'success' ? 'text-success-600' :
                  platform.status === 'error' ? 'text-danger-600' : 'text-warning-600'
                }`}>
                  {platform.status}
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Last Sync:</span>
                <span className="text-gray-900">
                  {platform.run_timestamp ? new Date(platform.run_timestamp).toLocaleString() : 'Never'}
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Records:</span>
                <span className="text-gray-900">{platform.records_processed || 0}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duration:</span>
                <span className="text-gray-900">{platform.duration_ms ? `${platform.duration_ms}ms` : '-'}</span>
              </div>
            </div>
            
            <button
              onClick={() => syncMutation.mutate(platform.platform)}
              disabled={syncMutation.isLoading}
              className="btn btn-secondary w-full mt-4"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isLoading ? 'animate-spin' : ''}`} />
              Sync Now
            </button>
          </div>
        ))}
      </div>

      {/* Sync History */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Sync History</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Records
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {syncHistory?.data?.map((sync: any) => (
                <tr key={sync.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium capitalize">{sync.platform}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(sync.status)}
                      <span className={`ml-2 text-sm font-medium ${
                        sync.status === 'success' ? 'text-success-600' :
                        sync.status === 'error' ? 'text-danger-600' : 'text-warning-600'
                      }`}>
                        {sync.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(sync.run_timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sync.records_processed || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {sync.duration_ms ? `${sync.duration_ms}ms` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Sync 